import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Header from '../HeaderSection/Header';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { isValidEmail, formatCurrency } from '../../utils/helpers';
import { locationUtils } from '../../utils/locationUtils';
import { encrypt } from '../../utils/encrypt';
import { SALESPINCH_API_URL, SHOW_PLAN_INFO } from '../../constants';
import ErrorMessage from '../common/ErrorMessage';
import SuccessMessage from '../common/SuccessMessage';
import PaymentWrapper from '../PaymentSection/PaymentWrapper';
import { trackRegistration, trackPaymentStart, trackPaymentSuccess, trackPaymentFailure } from '../../services/analyticsService';
import { mapCountryCodeForDB } from '../../utils/helpers';

const Signup = () => {
    const history = useHistory();
    const location = useLocation();
    const { register, isLoading, error } = useAuth();
    const { loadPlans, plans, billingCycle } = useSubscriptions();
    
    // Get plan from URL query parameter
    const urlParams = new URLSearchParams(location.search);
    const selectedPlanId = urlParams.get('plan');
    const selectedBillingCycle = urlParams.get('billing_cycle') || 'Monthly';
    
    const [selectedPlan, setSelectedPlan] = useState(null);

    const [formData, setFormData] = useState({
        client_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        acceptTerms: false
    });

    const [validationErrors, setValidationErrors] = useState({});
    const [registered, setRegistered] = useState(false);
    const [clientData, setClientData] = useState(null);
    const [showPayment, setShowPayment] = useState(false);
    const [paymentCompleted, setPaymentCompleted] = useState(false);
    const [registrationError, setRegistrationError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [userCountry, setUserCountry] = useState(null);

    // Populate form data from URL parameters if available
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const prefillData = {
            client_name: urlParams.get('company_name') || '',
            contact_name: urlParams.get('contact_name') || '',
            contact_email: urlParams.get('contact_email') || '',
            contact_phone: urlParams.get('contact_phone') || ''
        };
        
        // Only update if we have some prefill data
        if (Object.values(prefillData).some(value => value)) {
            setFormData(prev => ({
                ...prev,
                ...prefillData
            }));
        }
    }, [location.search]);

    // Load plans for the selected billing cycle and detect user's country
    useEffect(() => {
        const initializeData = async () => {
            try {
                console.log('Signup: Initializing with planId:', selectedPlanId, 'billingCycle:', selectedBillingCycle);
                
                // Load plans for the selected billing cycle if we have a plan ID
                if (selectedPlanId && selectedBillingCycle) {
                    console.log('Signup: Loading plans for billing cycle:', selectedBillingCycle);
                    await loadPlans(selectedBillingCycle);
                }
                
                // Detect user's country for payment gateway selection
                const countryData = await locationUtils.detectUserCountry();
                setUserCountry(countryData);
            } catch (error) {
                console.warn('Failed to initialize data:', error);
            }
        };
        
        initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPlans; plan + billing selection drive this fetch
    }, [selectedPlanId, selectedBillingCycle]);

    // Find the selected plan after plans are loaded
    useEffect(() => {
        console.log('Signup: Finding plan. selectedPlanId:', selectedPlanId, 'plans.length:', plans.length, 'current billingCycle:', billingCycle, 'selectedBillingCycle:', selectedBillingCycle);
        if (selectedPlanId && plans.length > 0) {
            const planId = Number(selectedPlanId);
            console.log('Signup: Looking for plan with ID:', planId);
            console.log('Signup: Available plans:', plans.map(p => ({ id: p.id, name: p.plan_name, cycle: p.billing_cycle })));
            
            // Only look for the plan if the current billing cycle matches the selected billing cycle
            if (billingCycle === selectedBillingCycle) {
                const plan = plans.find(p => p.id === planId);
                console.log('Signup: Found plan:', plan);
                setSelectedPlan(plan || null);
            } else {
                console.log('Signup: Billing cycle mismatch, waiting for correct cycle to load');
            }
        } else {
            setSelectedPlan(null);
        }
    }, [selectedPlanId, plans, billingCycle, selectedBillingCycle]);

    const validateForm = () => {
        const errors = {};
        console.log('Running validateForm with:', formData);

        if (!formData.client_name.trim()) {
            errors.client_name = 'Company name is required';
        }

        if (!formData.contact_name.trim()) {
            errors.contact_name = 'Contact name is required';
        }

        if (!formData.contact_email.trim()) {
            errors.contact_email = 'Email is required';
        } else if (!isValidEmail(formData.contact_email)) {
            errors.contact_email = 'Please enter a valid email address';
        }

        if (!formData.contact_phone.trim()) {
            errors.contact_phone = 'Phone number is required';
        } else         if (!/^\+[1-9][0-9]{7,15}$/.test(formData.contact_phone)) {
            errors.contact_phone = 'Please enter a valid phone number with country code (e.g., +1 or +91).';
        }

        if (!formData.acceptTerms) {
            errors.acceptTerms = 'You must accept the Terms of Service and Privacy Policy to continue';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsProcessing(true);
        setRegistrationError(null);

        try {
            // Determine country code and currency based on user's selection or auto-detection
            let countryCode = 'US';
            let currencyCode = 'USD';
            
            // Check if user has explicitly selected a country (from CountrySelector)
            const cachedCountry = locationUtils.getCachedCountry();
            if (cachedCountry && cachedCountry.countryCode) {
                // User has selected a country
                if (cachedCountry.countryCode === 'IN') {
                    // India selected
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else if (cachedCountry.countryCode === 'GLOBAL') {
                    // Global selected - use US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                } else {
                    // Other country selected - use the selected values
                    countryCode = mapCountryCodeForDB(cachedCountry.countryCode);
                    currencyCode = cachedCountry.currency || 'USD';
                }
            } else if (userCountry && userCountry.countryCode) {
                // No explicit selection - use auto-detected country
                // Only India gets special treatment, everything else defaults to US/USD
                if (userCountry.countryCode === 'IN') {
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else {
                    // All other countries default to US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                }
            }
            // Otherwise, defaults to US/USD

            // Prepare registration data with plan information and country/currency
            const registrationData = {
                ...formData,
                country_code: countryCode,
                currency_code: currencyCode,
                plan_data: selectedPlan ? {
                    id: selectedPlan.id, // plan_prices.id (main identifier)
                    plan_id: selectedPlan.plan_id, // subscription_plans.id (for reference)
                    plan_name: selectedPlan.plan_name,
                    base_price: selectedPlan.base_price,
                    user_limit: selectedPlan.user_limit,
                    ai_tokens_limit: selectedPlan.ai_tokens_limit,
                    billing_cycle: selectedPlan.billing_cycle || selectedBillingCycle
                } : null
            };

            console.log('Registration data being sent:', registrationData);
            const result = await register(registrationData);
            
            if (result.success) {
                setClientData(result.data);
                
                // Track registration
                const planType = selectedPlan ? selectedPlan.plan_name : 'Free';
                trackRegistration(planType);
                
                // If it's a paid plan, show payment flow
                if (selectedPlan && selectedPlan.base_price > 0) {
                    setShowPayment(true);
                    // Track payment start with correct currency
                    const currency = userCountry?.currency || 'USD';
                    trackPaymentStart(selectedPlan.plan_name, selectedPlan.base_price, currency);
                } else {
                    // Free plan - complete registration
                    await completeRegistration(result.data);
                    //setRegistered(true);
                    //setPaymentCompleted(true);
                }
            } else {
                setRegistrationError('Registration failed. Please try again or contact support if the issue persists.');
            }
        } catch (err) {
            console.error('Registration error:', err);
            setRegistrationError('An unexpected error occurred during registration. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const completeRegistration = async (clientData) => {
        try {
            // Determine country code and currency based on user's selection or auto-detection
            let countryCode = 'US';
            let currencyCode = 'USD';
            
            // Check if user has explicitly selected a country (from CountrySelector)
            const cachedCountry = locationUtils.getCachedCountry();
            if (cachedCountry && cachedCountry.countryCode) {
                // User has selected a country
                if (cachedCountry.countryCode === 'IN') {
                    // India selected
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else if (cachedCountry.countryCode === 'GLOBAL') {
                    // Global selected - use US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                } else {
                    // Other country selected - use the selected values
                    countryCode = mapCountryCodeForDB(cachedCountry.countryCode);
                    currencyCode = cachedCountry.currency || 'USD';
                }
            } else if (userCountry && userCountry.countryCode) {
                // No explicit selection - use auto-detected country
                // Only India gets special treatment, everything else defaults to US/USD
                if (userCountry.countryCode === 'IN') {
                    countryCode = 'IN';
                    currencyCode = 'INR';
                } else {
                    // All other countries default to US/USD
                    countryCode = 'US';
                    currencyCode = 'USD';
                }
            }
            // Otherwise, defaults to US/USD

            const salespinchPayload = {
                client_name: clientData.client_name,
                first_name: formData.contact_name,
                mobile_no: clientData.contact_phone,
                email: clientData.contact_email,
                client_api_key: clientData.api_key,
                country_code: countryCode,
                currency_code: currencyCode,
                client_identifier: clientData.client_identifier
            };
            console.log(salespinchPayload);
            const encryptedPayload = encrypt(JSON.stringify(salespinchPayload));
            
            const salespinchResponse = await fetch(`${SALESPINCH_API_URL}/clients`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ data: encryptedPayload }),
            });

            if (!salespinchResponse.ok) {
                const errorData = await salespinchResponse.json().catch(() => ({ message: 'Failed to sync with SalesPinch. Invalid JSON response.' }));
                throw new Error(errorData.message || 'Failed to sync with SalesPinch');
            }
            
            const salespinchResult = await salespinchResponse.json();

            if (salespinchResult.status !== 'success' || salespinchResult.data?.status !== 'success') {
                throw new Error(salespinchResult.message || salespinchResult.data?.message || 'SalesPinch API returned an error');
            }

            setRegistered(true);
            setRegistrationError(null);

        } catch (err) {
            console.error("Failed to call salespinch api", err);
            setRegistrationError('Your account was created successfully, but there was an issue syncing with our systems. Please contact support with your API key to complete the setup.');
            // Still show success but with warning about sync issue
            setRegistered(true);
        }
    };

    const handlePaymentSuccess = async (paymentData) => {
        console.log('Payment success, completing registration...');
        setPaymentCompleted(true);
        // Track successful payment with correct currency
        if (selectedPlan) {
            const currency = userCountry?.currency || 'USD';
            trackPaymentSuccess(selectedPlan.plan_name, selectedPlan.base_price, currency);
        }
        // Complete registration after successful payment
        await completeRegistration(clientData);
    };

    const handlePaymentError = (error) => {
        // Track payment failure
        if (selectedPlan) {
            trackPaymentFailure(selectedPlan.plan_name, error.message || 'Unknown error');
        }
        setRegistrationError('Payment processing failed. Please try again or contact support if the issue persists.');
        setShowPayment(false);
    };

    const handlePaymentCancel = () => {
        setShowPayment(false);
        history.push(SHOW_PLAN_INFO ? '/pricing' : '/');
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear validation error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleContinueToDashboard = () => {
        // Redirect to dashboard or main application
        window.location.href = 'https://app.salespinch.com/dashboard';
    };

    const handleGoToPricing = () => {
        history.push('/pricing');
    };

    // Show payment flow
    if (showPayment && !paymentCompleted) {
        return (
            <div className="accounts inner-pages signup">
                <div className="main">
                    <Header imageData={"/img/logo-white.png"} />
                    <section id="home" className="section welcome-area h-100vh bg-overlay d-flex align-items-center">
                        <div className="container">
                            <PaymentWrapper
                                planData={selectedPlan}
                                customerData={formData}
                                onSuccess={handlePaymentSuccess}
                                onError={handlePaymentError}
                                onCancel={handlePaymentCancel}
                            />
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    // Show loading state while completing registration after payment
    if (paymentCompleted && !registered) {
        return (
            <div className="accounts inner-pages signup">
                <div className="main">
                    <Header imageData={"/img/logo-white.png"} />
                    <section id="home" className="section welcome-area h-100vh bg-overlay d-flex align-items-center">
                        <div className="container">
                            <div className="row align-items-center justify-content-center">
                                <div className="col-12 col-md-8 col-lg-6 text-center">
                                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                    <h4 className="text-white">Completing Your Registration</h4>
                                    <p className="text-white">Please wait while we set up your account...</p>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    if (registered) {
        return (
            <div className="accounts inner-pages signup">
                <div className="main">
                    <Header imageData={"/img/logo-white.png"} />
                    <section id="home" className="section welcome-area h-100vh bg-overlay d-flex align-items-center">
                        <div className="container">
                            <div className="row align-items-center justify-content-center">
                                <div className="col-12 col-md-8 col-lg-6">
                                    <SuccessMessage
                                        title="Registration Successful!"
                                        message={<>
                                            {paymentCompleted && (
                                                <>
                                                    ✅ Payment processed successfully!
                                                    <br /><br />
                                                </>
                                            )}
                                            {registrationError ? (
                                                <>
                                                    ⚠️ {registrationError}
                                                    <br /><br />
                                                    Your API Key: <code>{clientData?.api_key}</code>
                                                    <br /><br />
                                                    Please contact support with this API key to complete your setup.
                                                </>
                                            ) : (
                                                <>
                                                    We've sent your login credentials and activation details to <strong>{clientData?.contact_email}</strong>.
                                                    <br /><br />
                                                    📩 Please check your inbox (and spam folder just in case) for an email titled "Welcome to SalesPinch".
                                                    <br /><br />
                                                    Inside, you'll find:
                                                    <br />
                                                    ✅ Your temporary password
                                                    <br />
                                                    ✅ A link to log in and get started
                                                </>
                                            )}
                                        </>}
                                        apiKey={clientData?.api_key}
                                        onContinue={handleContinueToDashboard}
                                        onGoToPricing={handleGoToPricing}
                                        showPricingButton={SHOW_PLAN_INFO && !selectedPlan}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Shape Bottom */}
                        <div className="shape-bottom">
                            <svg viewBox="0 0 1920 310" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" className="svg replaced-svg">
                                <title>SalesPinch Shape</title>
                                <desc>Created with Sketch</desc>
                                <defs />
                                <g id="SalesPinch-Landing-Page" stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                                    <g id="SalesPinch-v1.0" transform="translate(0.000000, -554.000000)" fill="#FFFFFF">
                                        <path d="M-3,551 C186.257589,757.321118 319.044414,856.322454 395.360475,848.004007 C509.834566,835.526337 561.525143,796.329212 637.731734,765.961549 C713.938325,735.593886 816.980646,681.910577 1035.72208,733.065469 C1254.46351,784.220361 1511.54925,678.92359 1539.40808,662.398665 C1567.2669,645.87374 1660.9143,591.478574 1773.19378,597.641868 C1848.04677,601.75073 1901.75645,588.357675 1934.32284,557.462704 L1934.32284,863.183395 L-3,863.183395" id="SalesPinch-v1.0" />
                                    </g>
                                </g>
                            </svg>
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    return (
        <div className="accounts inner-pages signup">
            <div className="main">
                <Header imageData={"/img/logo-white.png"} />
                <section id="home" className="section welcome-area h-100vh bg-overlay d-flex align-items-center">
                    <div className="container">
                        <div className="row align-items-center justify-content-center">
                            {/* Welcome Intro Start */}
                            <div className="col-12 col-lg-7">
                                <div className="welcome-intro">
                                    <h1 className="text-white">Create an account!</h1>
                                    <p className="text-white my-4">
                                        Join thousands of companies using MobiCloud. Get started with our powerful API and AI services.
                                    </p>
                                </div>
                            </div>
                            <div className="col-12 col-md-8 col-lg-5">
                                {/* Contact Box */}
                                <div className="contact-box bg-white text-center rounded p-4 p-sm-5 mt-5 mt-lg-0 shadow-lg">
                                    {/* Contact Form */}
                                    <form onSubmit={handleSubmit} noValidate>
                                        <div className="contact-top">
                                            <h3 className="contact-title">Sign Up</h3>
                                            {/* <h6 className="text-secondary fw-3 py-3">Fill all fields so we can get some info about you. We'll never send you spam</h6> */}
                                            {SHOW_PLAN_INFO && selectedPlan && (
                                                <div className="alert alert-info">
                                                    <small>
                                                        Selected Plan: <strong>{selectedPlan.plan_name}</strong>
                                                        {selectedPlan.base_price > 0 && (
                                                            <span> - {formatCurrency(selectedPlan.base_price, selectedPlan.currency || 'USD')}/{selectedPlan.billing_cycle === 'Yearly' ? 'year' : 'month'}</span>
                                                        )}
                                                        <br />
                                                        <span className="text-muted">Billing Cycle: {selectedPlan.billing_cycle}</span>
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Error Messages */}
                                        {error && (
                                            <ErrorMessage error={error} />
                                        )}
                                        {registrationError && (
                                            <ErrorMessage error={registrationError} />
                                        )}
                                        
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <div className="input-group">
                                                        <div className="input-group-prepend">
                                                            <span className="input-group-text"><i className="fas fa-building" /></span>
                                                        </div>
                                                        <input 
                                                            type="text" 
                                                            className={`form-control ${validationErrors.client_name ? 'is-invalid' : ''}`}
                                                            name="client_name"
                                                            value={formData.client_name}
                                                            onChange={handleChange}
                                                            placeholder="Company Name" 
                                                            required="required" 
                                                        />
                                                    </div>
                                                    {validationErrors.client_name && (
                                                        <div className="invalid-feedback d-block">{validationErrors.client_name}</div>
                                                    )}
                                                </div>
                                                <div className="form-group">
                                                    <div className="input-group">
                                                        <div className="input-group-prepend">
                                                            <span className="input-group-text"><i className="fas fa-user" /></span>
                                                        </div>
                                                        <input 
                                                            type="text" 
                                                            className={`form-control ${validationErrors.contact_name ? 'is-invalid' : ''}`}
                                                            name="contact_name"
                                                            value={formData.contact_name}
                                                            onChange={handleChange}
                                                            placeholder="Contact Name" 
                                                            required="required" 
                                                        />
                                                    </div>
                                                    {validationErrors.contact_name && (
                                                        <div className="invalid-feedback d-block">{validationErrors.contact_name}</div>
                                                    )}
                                                </div>
                                                <div className="form-group">
                                                    <div className="input-group">
                                                        <div className="input-group-prepend">
                                                            <span className="input-group-text"><i className="fas fa-envelope-open" /></span>
                                                        </div>
                                                        <input 
                                                            type="email" 
                                                            className={`form-control ${validationErrors.contact_email ? 'is-invalid' : ''}`}
                                                            name="contact_email"
                                                            value={formData.contact_email}
                                                            onChange={handleChange}
                                                            placeholder="Email" 
                                                            required="required" 
                                                        />
                                                    </div>
                                                    {validationErrors.contact_email && (
                                                        <div className="invalid-feedback d-block">{validationErrors.contact_email}</div>
                                                    )}
                                                </div>
                                                <div className="form-group">
                                                    <div className="input-group">
                                                        <div className="input-group-prepend">
                                                            <span className="input-group-text"><i className="fas fa-phone" /></span>
                                                        </div>
                                                        <input 
                                                            type="tel" 
                                                            className={`form-control ${validationErrors.contact_phone ? 'is-invalid' : ''}`}
                                                            name="contact_phone"
                                                            value={formData.contact_phone}
                                                            onChange={handleChange}
                                                            placeholder="Phone Number (with country code)"
                                                            required
                                                        />
                                                    </div>
                                                    {/*<small className="form-text text-muted">Please enter your phone number with country code (e.g., +1 or +91).</small>*/}
                                                    {validationErrors.contact_phone && (
                                                        <div className="invalid-feedback d-block">{validationErrors.contact_phone}</div>
                                                    )}
                                                </div>
                                                <div className="form-group form-check">
                                                    <input 
                                                        type="checkbox" 
                                                        className={`form-check-input ${validationErrors.acceptTerms ? 'is-invalid' : ''}`}
                                                        name="acceptTerms"
                                                        id="termsCheck" 
                                                        checked={formData.acceptTerms}
                                                        onChange={handleChange}
                                                        required 
                                                    />
                                                    <label className="form-check-label contact-bottom" htmlFor="termsCheck">
                                                        <span className="d-inline-block mt-3">By signing up, you accept our <a href="/terms">Terms</a> &amp; <a href="/privacy">Privacy Policy</a></span>
                                                    </label>
                                                    {validationErrors.acceptTerms && (
                                                        <div className="invalid-feedback d-block">{validationErrors.acceptTerms}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <button 
                                                    className="btn btn-bordered w-100 mt-3" 
                                                    type="submit"
                                                    disabled={isLoading || isProcessing}
                                                >
                                                    {isLoading || isProcessing ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                            {isProcessing ? 'Processing...' : 'Creating Account...'}
                                                        </>
                                                    ) : (
                                                        selectedPlan && selectedPlan.base_price > 0 ? 'Continue to Payment' : 'Create Account'
                                                    )}
                                                </button>
                                            </div>
                                            {/*<div className="col-12">
                                                <span className="d-block pt-2 mt-4 border-top">Already have an account? <a href="https://app.salespinch.com/login">Sign In</a></span>
                                            </div>*/}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Shape Bottom */}
                    <div className="shape-bottom">
                        <svg viewBox="0 0 1920 310" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" className="svg replaced-svg">
                            <title>SalesPinch Shape</title>
                            <desc>Created with Sketch</desc>
                            <defs />
                            <g id="SalesPinch-Landing-Page" stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
                                <g id="SalesPinch-v1.0" transform="translate(0.000000, -554.000000)" fill="#FFFFFF">
                                    <path d="M-3,551 C186.257589,757.321118 319.044414,856.322454 395.360475,848.004007 C509.834566,835.526337 561.525143,796.329212 637.731734,765.961549 C713.938325,735.593886 816.980646,681.910577 1035.72208,733.065469 C1254.46351,784.220361 1511.54925,678.92359 1539.40808,662.398665 C1567.2669,645.87374 1660.9143,591.478574 1773.19378,597.641868 C1848.04677,601.75073 1901.75645,588.357675 1934.32284,557.462704 L1934.32284,863.183395 L-3,863.183395" id="SalesPinch-v1.0" />
                                </g>
                            </g>
                        </svg>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default Signup;